import type { LayoutResizeHandle } from "~/v3-poc/features/workspace/shared/layoutSettings/layoutSettings.types";
import {
  REACT_GRID_LAYOUT_SCHEMA,
  type RglSettingId,
} from "./reactGridLayoutSettings.schema";
import type { ReactGridLayoutSettings } from "./reactGridLayoutSettings.types";

const AT_LEAST_ONE_HANDLE = "At least one resize handle is required.";
const MAX_BELOW_MIN_W =
  "Global max width cannot be below the enabled global min width.";
const MAX_BELOW_MIN_H =
  "Global max height cannot be below the enabled global min height.";
const MIN_ABOVE_MAX_W =
  "Global min width cannot exceed the enabled global max width.";
const MIN_ABOVE_MAX_H =
  "Global min height cannot exceed the enabled global max height.";
const ENABLE_WOULD_VIOLATE_W =
  "Enabling this would leave global min width above global max width.";
const ENABLE_WOULD_VIOLATE_H =
  "Enabling this would leave global min height above global max height.";

export interface RglDisabledState {
  disabled: boolean;
  reason?: string;
}

export const disabledForRglSetting = (
  c: ReactGridLayoutSettings,
  id: RglSettingId,
): RglDisabledState => {
  const descriptor = REACT_GRID_LAYOUT_SCHEMA[id];
  if (descriptor.status === "unavailable") {
    return { disabled: true, reason: descriptor.unavailableReason };
  }
  switch (id) {
    case "containerPaddingX":
    case "containerPaddingY":
      return c.containerPaddingMode === "margin"
        ? {
            disabled: true,
            reason: "Enabled when container padding mode is custom.",
          }
        : { disabled: false };
    case "maxRows":
      return c.maxRows.limited
        ? { disabled: false }
        : { disabled: true, reason: "Enabled when row limit is on." };
    case "positionScale":
      return c.positionStrategy === "scaled"
        ? { disabled: false }
        : {
            disabled: true,
            reason: "Enabled when position strategy is scaled.",
          };
    case "globalMinW":
    case "globalMinH":
      return c.globalMin.enabled
        ? { disabled: false }
        : {
            disabled: true,
            reason: "Enabled when global minimum size is on.",
          };
    case "globalMaxW":
    case "globalMaxH":
      return c.globalMax.enabled
        ? { disabled: false }
        : {
            disabled: true,
            reason: "Enabled when global maximum size is on.",
          };
    case "snapX":
    case "snapY":
      return c.snap.enabled
        ? { disabled: false }
        : { disabled: true, reason: "Enabled when snap to grid is on." };
    case "aspectRatio":
      return c.aspect.enabled
        ? { disabled: false }
        : {
            disabled: true,
            reason: "Enabled when aspect ratio lock is on.",
          };
    default:
      return { disabled: false };
  }
};

export const numericRglCrossFieldError = (
  next: ReactGridLayoutSettings,
  id: RglSettingId,
): string | undefined => {
  if (!(next.globalMin.enabled && next.globalMax.enabled)) {
    return undefined;
  }
  switch (id) {
    case "globalMaxW":
      return next.globalMax.w < next.globalMin.w ? MAX_BELOW_MIN_W : undefined;
    case "globalMaxH":
      return next.globalMax.h < next.globalMin.h ? MAX_BELOW_MIN_H : undefined;
    case "globalMinW":
      return next.globalMin.w > next.globalMax.w ? MIN_ABOVE_MAX_W : undefined;
    case "globalMinH":
      return next.globalMin.h > next.globalMax.h ? MIN_ABOVE_MAX_H : undefined;
    default:
      return undefined;
  }
};

export const rglEnableToggleInvariantError = (
  next: ReactGridLayoutSettings,
): string | undefined => {
  if (!(next.globalMin.enabled && next.globalMax.enabled)) {
    return undefined;
  }
  if (next.globalMin.w > next.globalMax.w) {
    return ENABLE_WOULD_VIOLATE_W;
  }
  if (next.globalMin.h > next.globalMax.h) {
    return ENABLE_WOULD_VIOLATE_H;
  }
  return undefined;
};

export const rglResizeHandlesInvariantError = (
  candidate: ReadonlyArray<LayoutResizeHandle>,
): string | undefined =>
  candidate.length === 0 ? AT_LEAST_ONE_HANDLE : undefined;
